// MoveEventDialog — move an event to a different event category.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { eventsApi } from '@/lib/api/events'
import { categoriesApi, type Category } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { HierarchyPickerDialog, type HierarchyNode } from '@/components/shared/HierarchyPickerDialog'

interface MoveEventDialogProps {
  open: boolean
  onClose: () => void
  onMoved: () => void
  eventId: string
  currentCategoryId: string
  currentCategoryName: string
}

export function MoveEventDialog({
  open,
  onClose,
  onMoved,
  eventId,
  currentCategoryId,
  currentCategoryName,
}: MoveEventDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const evtApi = eventsApi(api)
  const catApi = categoriesApi(api, 'event')

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'event'),
    queryFn: () => catApi.tree(),
    enabled: open,
  })

  const moveMutation = useMutation({
    mutationFn: ({ destId, notes }: { destId: string; notes: string }) =>
      evtApi.move(eventId, destId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'event') })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(slug) })
      onMoved()
    },
  })

  const nodes: HierarchyNode[] = (categories ?? []).map((c: Category) => ({
    id: c.id,
    name: c.name,
    parent_id: c.parent_id,
  }))

  function handleConfirm(selectedId: string, changeNotes: string) {
    moveMutation.mutate({ destId: selectedId, notes: changeNotes })
  }

  return (
    <HierarchyPickerDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Move Event"
      subtitle={`Current category: ${currentCategoryName}`}
      nodeLabel="category"
      nodes={nodes}
      currentId={currentCategoryId}
      isPending={moveMutation.isPending}
      error={moveMutation.error ? (moveMutation.error as Error).message : null}
    />
  )
}
